import type { CSSProperties } from 'react';

import type { RegimeCellMetricValuesDto } from 'api/generated/schemas';

import type { MetricKind } from '../results-step/results-step.types';

/**
 * Column geometry shared by the award-calculation matrix and every comparison grid below it.
 * The design puts all of those tables inside one horizontal scroll container, so they only line
 * up if they derive their widths from the same constants - keep them here rather than per-file.
 */
export const RANK_COLUMN_WIDTH_PX = 48;
export const RANGE_COLUMN_WIDTH_PX = 160;

/**
 * Total columns - the Rank column plus the data columns - that still fit without scrolling. Up to
 * this many, columns stretch to fill the available width; beyond it they fall back to a fixed
 * width and the shared container scrolls horizontally.
 */
export const MAX_COLUMNS_WITHOUT_SCROLL = 7;

export const RANK_COLUMN_STYLE: CSSProperties = {
  width: `${RANK_COLUMN_WIDTH_PX}px`,
};

interface ColumnSizing {
  className: string;
  style?: CSSProperties;
}

/**
 * Every grid in the shared scroll container must size its columns the same way, otherwise the
 * stacked tables stop lining up - so both the matrix and the comparison grids read their column
 * sizing from here off one shared decision.
 */
export const getColumnSizing = (isFixedWidth: boolean): ColumnSizing =>
  isFixedWidth
    ? { className: 'shrink-0', style: { width: `${RANGE_COLUMN_WIDTH_PX}px` } }
    : { className: 'flex-1 min-w-0' };

export interface ComparisonMetric {
  key: keyof RegimeCellMetricValuesDto;
  label: string;
  kind: MetricKind;
}

/**
 * One accordion per metric, ordered as the user story lists them. Each grid repeats the
 * award-calculation matrix layout (academic-rank rows x need-bucket columns) and splits every
 * bucket column into the current-year and future-year tracks the API returns per cell.
 */
export const COMPARISON_METRICS = [
  { key: 'netCost', label: 'Net cost', kind: 'currency' },
  { key: 'netTuition', label: 'Net tuition', kind: 'currency' },
  {
    key: 'avgInstitutionalAid',
    label: 'Avg institutional aid',
    kind: 'currency',
  },
  { key: 'yieldRate', label: 'Yield', kind: 'rate' },
  { key: 'admits', label: 'Admits', kind: 'count' },
  { key: 'enrolls', label: 'Enrolls', kind: 'count' },
] as const satisfies readonly ComparisonMetric[];

export const CURRENT_YEAR_LABEL = 'Current';
export const FUTURE_YEAR_LABEL = 'Future';
